/**
 * 图形属性面板：矢量（心形/多边形/星形/路径）、基础几何（线/矩形/圆/三角形）与涂抹笔。
 * 外观统一由 AppearanceSettings 管理。支持文字图形（circle/rect/triangle/vector）的文字样式设置。
 */
import type { ReactNode } from 'react';
import { Label, ListBox, Select } from '@heroui/react';
import { NumberStepper } from '@heroui-pro/react/number-stepper';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type {
  CircleElement,
  ElementPatch,
  FreehandElement,
  LineElement,
  RectangleElement,
  TriangleElement,
  VectorElement,
} from '@/components/canvas/document/publicTypes';
import { boxSizeControlPatchToElementPatch, layoutControlPatchToElementPatch } from '@/components/canvas/elements/model';
import {
  AppearanceSettings,
  BoxSizeSettings,
  LayoutSettings,
  TextSettings,
  appearanceValueFromAppearance,
  appearanceValueToAppearancePatch,
  boxSizeSettingsValueFromElement,
  layoutSettingsValueFromElement,
  type AppearanceSettingsValue,
  type BoxSizeSettingsValue,
  type CanvasAlignAction,
  type LayoutSettingsValue,
  type TextSettingsPatch,
} from '@/components/canvas/shared/controls';
import {
  SHAPE_DEFAULT_FONT_SIZE,
  SHAPE_DEFAULT_TEXT_COLOR,
} from '@/components/canvas/shared/constants/legacyConfig';

export type GraphicElement =
  | LineElement
  | RectangleElement
  | CircleElement
  | TriangleElement
  | VectorElement
  | FreehandElement;

/** 支持内嵌文字的图形元素类型 */
type TextSupportedElement = RectangleElement | CircleElement | TriangleElement | VectorElement;

function isTextSupported(el: GraphicElement): el is TextSupportedElement {
  return el.type === 'rect' || el.type === 'circle' || el.type === 'triangle' || el.type === 'vector';
}

export interface VectorPropertiesProps {
  element?: GraphicElement;
  /** 涂抹笔工具激活且未选中元素时，编辑下笔默认外观 */
  freehandToolDefaults?: boolean;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function VectorNumberRow({
  label,
  ariaLabel,
  value,
  minValue,
  maxValue,
  step,
  onChange,
}: {
  label: string;
  ariaLabel: string;
  value: number;
  minValue: number;
  maxValue: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="grid w-full grid-cols-[1fr_auto] items-center gap-3 py-0.5">
      <Label>{label}</Label>
      <NumberStepper
        aria-label={ariaLabel}
        size="sm"
        className="justify-self-end"
        value={value}
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        onChange={(v) => {
          if (v == null || Number.isNaN(v)) return;
          onChange(v);
        }}
      >
        <NumberStepper.Group>
          <NumberStepper.DecrementButton />
          <NumberStepper.Value />
          <NumberStepper.IncrementButton />
        </NumberStepper.Group>
      </NumberStepper>
    </div>
  );
}

function VectorShapeSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="w-full shrink-0 space-y-2 p-1">
      <h3 className="text-sm font-semibold text-default-foreground">{title}</h3>
      <div className="space-y-0.5">{children}</div>
    </section>
  );
}

function FreehandToolDefaultsPanel() {
  const { freehandStrokeColor, setFreehandStrokeColor, freehandStrokeWidth, setFreehandStrokeWidth } =
    useCanvas();

  const appearanceValue: AppearanceSettingsValue = {
    stroke: {
      color: freehandStrokeColor,
      width: freehandStrokeWidth,
      style: 'solid',
    },
  };

  const handleAppearanceChange = (partial: Partial<AppearanceSettingsValue>) => {
    if (partial.stroke?.color !== undefined) setFreehandStrokeColor(partial.stroke.color);
    if (partial.stroke?.width !== undefined) setFreehandStrokeWidth(partial.stroke.width);
  };

  return (
    <div className="property-panel-view min-h-0 w-full shrink-0 space-y-4 p-1">
      <AppearanceSettings
        value={appearanceValue}
        onChange={handleAppearanceChange}
        strokeWidthMax={40}
      />
    </div>
  );
}

export function VectorProperties({ element, freehandToolDefaults }: VectorPropertiesProps) {
  const { updateElement, getCurrentPage } = useCanvas();

  if (freehandToolDefaults && !element) {
    return <FreehandToolDefaultsPanel />;
  }

  if (!element) {
    return (
      <div className="p-4 text-sm text-muted-foreground">请选中一个图形元素</div>
    );
  }

  const patch = (partial: ElementPatch) => updateElement(element.id, partial);
  const appearanceValue = appearanceValueFromAppearance(element.appearance);

  const handleLayoutChange = (partial: Partial<LayoutSettingsValue>) => {
    patch(layoutControlPatchToElementPatch(element, partial));
  };

  const handleBoxSizeChange = (partial: Partial<BoxSizeSettingsValue>) => {
    patch(boxSizeControlPatchToElementPatch(partial));
  };

  const handleAppearanceChange = (partial: Partial<AppearanceSettingsValue>) => {
    const next = { ...appearanceValue, ...partial };
    patch({
      appearance: { ...element.appearance, ...appearanceValueToAppearancePatch(next, element.appearance) },
    });
  };

  const handleAlignToCanvas = (action: CanvasAlignAction) => {
    const page = getCurrentPage();
    if (!page) return;
    const { width: cw, height: ch } = page.size;
    let x = element.layout.x;
    let y = element.layout.y;
    if (action === 'left') x = 0;
    if (action === 'right') x = cw - element.size.width;
    if (action === 'top') y = 0;
    if (action === 'bottom') y = ch - element.size.height;
    if (action === 'center') {
      x = (cw - element.size.width) / 2;
      y = (ch - element.size.height) / 2;
    }
    patch({ layout: { x, y } });
  };

  const shapeSection = (() => {
    if (element.type === 'line') {
      const lineEnd = element.data.end ?? { x: element.size.width, y: 0 };
      return (
        <VectorShapeSection title="线条">
          <VectorNumberRow
            label="终点 X"
            ariaLabel="直线终点 X"
            value={lineEnd.x}
            minValue={-9999}
            maxValue={9999}
            step={1}
            onChange={(v) => patch({ data: { end: { ...lineEnd, x: num(v) } } })}
          />
          <VectorNumberRow
            label="终点 Y"
            ariaLabel="直线终点 Y"
            value={lineEnd.y}
            minValue={-9999}
            maxValue={9999}
            step={1}
            onChange={(v) => patch({ data: { end: { ...lineEnd, y: num(v) } } })}
          />
        </VectorShapeSection>
      );
    }

    if (element.type === 'triangle') {
      const dir = element.data.direction ?? 'up';
      return (
        <VectorShapeSection title="三角形">
          <div className="grid w-full grid-cols-[1fr_auto] items-center gap-3 py-0.5">
            <Label>方向</Label>
            <Select
              aria-label="三角形方向"
              className="w-28 justify-self-end"
              value={dir}
              onChange={(key) => {
                if (key === 'up' || key === 'down' || key === 'left' || key === 'right') {
                  patch({ data: { direction: key } });
                }
              }}
            >
              <Select.Trigger className="h-8">
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  <ListBox.Item id="up" textValue="向上">
                    向上
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="down" textValue="向下">
                    向下
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="left" textValue="向左">
                    向左
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="right" textValue="向右">
                    向右
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ListBox>
              </Select.Popover>
            </Select>
          </div>
        </VectorShapeSection>
      );
    }

    if (element.type === 'vector') {
      const polygonSides = element.data.polygonSides ?? 6;
      const starPoints = element.data.starPoints ?? 5;
      const starInnerRatio = element.data.starInnerRatio ?? 0.45;

      if (element.data.shape === 'polygon') {
        return (
          <VectorShapeSection title="多边形">
            <VectorNumberRow
              label="边数"
              ariaLabel="多边形边数"
              value={polygonSides}
              minValue={3}
              maxValue={12}
              step={1}
              onChange={(v) =>
                patch({ data: { polygonSides: Math.max(3, Math.min(12, Math.round(v))) } })
              }
            />
          </VectorShapeSection>
        );
      }
      if (element.data.shape === 'star') {
        return (
          <VectorShapeSection title="星形">
            <VectorNumberRow
              label="角数"
              ariaLabel="星形角数"
              value={starPoints}
              minValue={4}
              maxValue={12}
              step={1}
              onChange={(v) =>
                patch({ data: { starPoints: Math.max(4, Math.min(12, Math.round(v))) } })
              }
            />
            <VectorNumberRow
              label="尖锐度"
              ariaLabel="星形尖锐度"
              value={starInnerRatio}
              minValue={0.1}
              maxValue={0.9}
              step={0.05}
              onChange={(v) =>
                patch({
                  data: { starInnerRatio: Math.max(0.1, Math.min(0.9, Math.round(v * 100) / 100)) },
                })
              }
            />
          </VectorShapeSection>
        );
      }
    }

    return null;
  })();

  const textSection = isTextSupported(element) ? (() => {
    const textSettingsValue = {
      fontFamily: element.typography.fontFamily,
      fontWeight: element.typography.fontWeight,
      fontSize: element.typography.fontSize ?? SHAPE_DEFAULT_FONT_SIZE,
      lineHeight: element.typography.lineHeight,
      letterSpacing: element.typography.letterSpacing,
      textColor: element.typography.color ?? SHAPE_DEFAULT_TEXT_COLOR,
      textAlign: element.paragraph.textAlign,
      verticalAlign: element.paragraph.verticalAlign,
      writingMode: element.paragraph.writingMode ?? 'horizontal-tb',
    };
    const handleTextChange = (p: TextSettingsPatch) => {
      patch({
        typography: {
          ...(p.fontFamily !== undefined ? { fontFamily: p.fontFamily } : {}),
          ...(p.fontWeight !== undefined ? { fontWeight: p.fontWeight } : {}),
          ...(p.fontSize !== undefined ? { fontSize: p.fontSize } : {}),
          ...(p.color !== undefined ? { color: p.color } : {}),
          ...(p.lineHeight !== undefined ? { lineHeight: p.lineHeight } : {}),
          ...(p.letterSpacing !== undefined ? { letterSpacing: p.letterSpacing } : {}),
        },
        paragraph: {
          ...(p.textAlign !== undefined ? { textAlign: p.textAlign } : {}),
          ...(p.verticalAlign !== undefined ? { verticalAlign: p.verticalAlign } : {}),
          ...('writingMode' in p ? { writingMode: p.writingMode } : {}),
        },
        data: { textStyle: undefined },
      });
    };
    return (
      <TextSettings
        title="文字"
        value={textSettingsValue}
        onChange={handleTextChange}
      />
    );
  })() : null;

  return (
    <div className="property-panel-view min-h-0 w-full shrink-0 space-y-4 p-1">
      {shapeSection}
      {textSection}
      <BoxSizeSettings value={boxSizeSettingsValueFromElement(element)} onChange={handleBoxSizeChange} />
      <LayoutSettings
        value={layoutSettingsValueFromElement(element)}
        onChange={handleLayoutChange}
        onAlignToCanvas={handleAlignToCanvas}
      />
      <AppearanceSettings value={appearanceValue} onChange={handleAppearanceChange} />
    </div>
  );
}
